import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import useHttpHook from "../../../Hooks/HttpHook";
import { AddbandId } from "../../../utils/ApiConstant";
import { getAttendeePhotoUrl, trimString } from "../../../utils/Helper";
import { DEFAULT_PROFILE_IMAGE } from "../../../utils/brandAssets";
import { OrderLanguage } from "../../../utils/Languages";
import MainComponentWrapper from "../../Common/MainComponentWrapper";
import MainPagination from "../../Common/MainPagination";
import ModalWrapper from "../../Common/Portals/ModalWrapper";
import { showErrorToast } from "../../../utils/notify";

const hasEventStarted = (eventTiming) => {
  const startDate = eventTiming?.startDate;
  if (!startDate) {
    return false;
  }
  return new Date(startDate) <= new Date();
};

const Attendee = ({
  list,
  isLoading,
  isError,
  getList,
  handleSearchChange,
  searchInput,
}) => {
  const [loading, sendRequest, error] = useHttpHook();
  const location = useLocation();
  const query = new URLSearchParams(window.location.search);
  const [showModel, setShowModel] = useState(false);
  const [attendee, setAttendee] = useState({});
  const [edit, setEdit] = useState(false);
  const { guard, user } = useSelector((state) => state);
  const eventName =
    query.get("event") !== null ? `?event=${query.get("event")}` : "";
  const items = list?.data?.itemsList || [];
  const isGuardView = location.pathname === "/guard/attendees";

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm();

  const handleModel = (selectedAttendee, isEdit = false) => {
    if (!hasEventStarted(selectedAttendee?.eventTiming)) {
      toast.error("Band ID cannot be assigned before the event start date.");
      return;
    }
    setAttendee(selectedAttendee);
    setEdit(isEdit);
    setValue("bandId", isEdit ? selectedAttendee?.bandId || "" : "");
    setShowModel(true);
  };

  useEffect(() => {
    if (showModel) {
      setValue("bandId", attendee?.bandId || "");
    }
  }, [attendee, showModel, setValue]);

  const handleBandSubmit = async (data) => {
    if (!hasEventStarted(attendee?.eventTiming)) {
      toast.error("Band ID cannot be assigned before the event start date.");
      setShowModel(false);
      return;
    }
    try {
      const requestConfig = {
        method: "patch",
        data: {
          bandId: data.bandId?.trim(),
          orderId: attendee?.orderId,
          attendeeId: attendee?._id,
        },
      };

      if (isGuardView && guard?.token) {
        requestConfig.guardToken = guard.token;
      }

      const response = await sendRequest(AddbandId, requestConfig);
      if (response?.status === 1) {
        toast.success(response?.message || "Band id added successfully");
        setShowModel(false);
        getList();
        return;
      }
      if (response?.status === 0) {
        showErrorToast(response?.message || "Unable to add band id");
      }
    } catch (submitError) {
      console.log(submitError);
    }
  };

  const getDetailsPath = (attendeeId) =>
    isGuardView
      ? `/guard/attendees/${attendeeId}`
      : `/organizer/attendees/${attendeeId}?redirectUrl=${location.pathname}${eventName}`;

  return (
    <MainComponentWrapper
      Loading={isLoading || loading}
      errorInfo={isError || error}
    >
      <form
        className="event-attendees-search"
        onSubmit={(event) => event.preventDefault()}
      >
        <label htmlFor="eventAttendeesSearch">
          Search for attendees by name or email or Band Id
        </label>
        <div className="event-attendees-search-wrap">
          <i className="bi bi-search" aria-hidden="true"></i>
          <input
            id="eventAttendeesSearch"
            className="event-attendees-search-input"
            type="text"
            name="q"
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search attendees"
            autoComplete="off"
          />
        </div>
      </form>

      <div className="event-attendees-table-wrap">
        {list !== null ? (
          items.length > 0 ? (
            <MainPagination
              getEventsMethod={getList}
              MainData={list}
              PaginationType="paginate"
            >
              <table className="event-attendees-table" aria-label="Attendee list">
                <thead>
                  <tr>
                    <th scope="col">
                      {OrderLanguage.Attendees?.list?.Columns?.photo}
                    </th>
                    <th scope="col">
                      {OrderLanguage.Attendees.list.Columns.eventName}
                    </th>
                    <th scope="col">
                      {OrderLanguage.Attendees.list.Columns.attendeeName}
                    </th>
                    <th scope="col">
                      {OrderLanguage.Attendees.list.Columns.email}
                    </th>
                    <th scope="col">
                      {OrderLanguage.Attendees.list.Columns.ticketType}
                    </th>
                    <th scope="col">
                      {OrderLanguage.Attendees.list.Columns.BandId}
                    </th>
                    <th scope="col" className="is-action">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, index) => {
                    const photoSrc = getAttendeePhotoUrl(row?.photo);
                    const eventStarted = hasEventStarted(row?.eventTiming);
                    const canManageBand =
                      eventStarted &&
                      row?.cancelRequestStatus !== "refunded" &&
                      (row?.isEmiPaid || row?.paymentType !== "emi");

                    return (
                      <tr
                        key={row._id || index}
                        className="event-attendees-row"
                        data-attendee-id={row._id}
                      >
                        <td className="event-attendees-cell-profile">
                          <img
                            className="event-attendees-profile"
                            src={photoSrc}
                            alt={row?.name || ""}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src =
                                DEFAULT_PROFILE_IMAGE;
                            }}
                          />
                        </td>
                        <td>{row ? trimString(row?.title, 40) : "—"}</td>
                        <td>{row?.name || "—"}</td>
                        <td>{row?.email || "—"}</td>
                        <td>{row?.ticketType || "—"}</td>
                        <td className="event-attendees-cell-band">
                          {row?.bandId || "—"}
                        </td>
                        <td className="event-attendees-cell-actions">
                          <Link
                            className="event-attendees-action-btn is-info"
                            to={getDetailsPath(row._id)}
                            aria-label={`View details for ${row?.name}`}
                          >
                            <span
                              className="event-attendees-action-mark"
                              aria-hidden="true"
                            >
                              i
                            </span>
                          </Link>
                          {canManageBand && !row.bandId && (
                            <button
                              type="button"
                              className="event-attendees-action-btn is-band is-add"
                              onClick={() => handleModel(row, false)}
                              aria-label={`Add band id for ${row?.name}`}
                            >
                              <i className="bi bi-plus-lg" aria-hidden="true"></i>
                            </button>
                          )}
                          {canManageBand && row.bandId && (
                            <button
                              type="button"
                              className="event-attendees-action-btn is-band is-edit"
                              onClick={() => handleModel(row, true)}
                              aria-label={`Edit band id for ${row?.name}`}
                            >
                              <i className="bi bi-pencil" aria-hidden="true"></i>
                            </button>
                          )}
                          {!eventStarted &&
                            row?.cancelRequestStatus !== "refunded" &&
                            (row?.isEmiPaid || row?.paymentType !== "emi") && (
                              <button
                                type="button"
                                className="event-attendees-band-locked"
                                aria-label="Band ID cannot be assigned before the event start date"
                                data-tooltip="Band ID cannot be assigned before the event start date"
                              >
                                <i className="bi bi-lock" aria-hidden="true"></i>
                              </button>
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MainPagination>
          ) : (
            <div className="event-attendees-empty">No attendees found</div>
          )
        ) : (
          <div className="event-attendees-empty">Select an event</div>
        )}
      </div>

      {showModel && (
        <ModalWrapper
          formId="band"
          title="Band Id"
          CloseModalHandler={() => setShowModel(false)}
          submitText={edit ? "Update" : "Add"}
          dialogClassName="event-attendees-modal-dialog"
          contentClassName="event-attendees-modal"
          headerClassName="event-attendees-modal-header"
          bodyClassName="event-attendees-modal-body"
          footerClassName="event-attendees-modal-footer"
          closeClass="event-attendees-modal-btn-close"
          submitClass="event-attendees-modal-btn-add"
        >
          <form id="band" onSubmit={handleSubmit(handleBandSubmit)}>
            <label className="event-attendees-modal-label" htmlFor="bandId">
              Band Id:
            </label>
            <input
              id="bandId"
              type="text"
              className="event-attendees-modal-input"
              {...register("bandId", { required: true })}
              autoComplete="off"
            />
            {errors.bandId && (
              <span className="text-danger">Band Id is required</span>
            )}
          </form>
        </ModalWrapper>
      )}
    </MainComponentWrapper>
  );
};

export default Attendee;
